import { getAssistantBriefingPath } from "@/lib/assistantBriefingLink";
import { downloadStoredFile } from "@/lib/directDownload";
import React, { useCallback, useEffect, useMemo, memo } from "react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  CheckCheck,
  Loader2,
  RefreshCcw,
  UserPlus,
  DownloadCloud,
  Layers,
  MapPin,
  BellRing,
  Briefcase,
  Eye,
  ArrowRight,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { useNotifications } from "@/hooks/useNotifications";
import { toast } from "sonner";
import { showError } from "@/lib/errorHandler";
import { isTrustedApiUrl, openNoOpener } from "@/lib/security";
import { resolveConfiguredApiUrl } from "@/lib/apiUrl";
import {
  formatNotificationTimeAgo,
  getLocalizedNotificationContent,
} from "@/lib/notificationFormatting";
import { createRealtimeNotificationToastBatcher } from "@/lib/realtimeNotificationToasts";
import type {
  LayerPostgisUpdate,
  LayerProcessingUpdate,
  NotificationSummary,
} from "@/types/notifications";

const MAX_TITLE_LENGTH = 80;

const truncate = (input: string, maxLength: number) => {
  if (input.length <= maxLength) return input;
  return `${input.slice(0, maxLength - 1)}…`;
};

const resolveDownloadApiUrl = (url: string) => {
  if (!url) return url;
  const match = url.match(/exports\/download\/([0-9a-f-]+)/i);
  if (match?.[1]) {
    return `/api/exports/download/${match[1]}`;
  }
  if (url.includes("/api/exports/download/")) {
    return url;
  }
  return url;
};

const downloadNotificationFile = async (url: string) => {
  if (!url) return;
  try {
    const resolved = resolveConfiguredApiUrl(resolveDownloadApiUrl(url));
    const isExternal =
      resolved.startsWith("https://") && !isTrustedApiUrl(resolved);
    if (isExternal) {
      openNoOpener(resolved);
      return;
    }
    await downloadStoredFile(resolved);
  } catch (err) {
    showError(err, "Descargar archivo");
  }
};

const getNotificationIcon = (type: string, _isUnread: boolean) => {
  const baseOuter =
    "shrink-0 flex items-center justify-center w-8 h-8 rounded-full transition-colors";
  switch (type) {
    case "project_created":
    case "project_updated":
      return (
        <div
          className={`${baseOuter} bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400`}
        >
          <Briefcase className="size-4" />
        </div>
      );
    case "member_added":
    case "member_removed":
    case "role_updated":
      return (
        <div
          className={`${baseOuter} bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400`}
        >
          <UserPlus className="size-4" />
        </div>
      );
    case "export_ready":
    case "export_completed":
    case "export_started":
      return (
        <div
          className={`${baseOuter} bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400`}
        >
          <DownloadCloud className="size-4" />
        </div>
      );
    case "layer_added":
    case "layer_updated":
    case "layer_deleted":
      return (
        <div
          className={`${baseOuter} bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400`}
        >
          <Layers className="size-4" />
        </div>
      );
    case "feature_added":
    case "feature_updated":
    case "feature_deleted":
      return (
        <div
          className={`${baseOuter} bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400`}
        >
          <MapPin className="size-4" />
        </div>
      );
    default:
      return (
        <div
          className={`${baseOuter} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300`}
        >
          <BellRing className="size-4" />
        </div>
      );
  }
};

interface NotificationItemProps {
  notification: NotificationSummary;
  onMarkAsRead: (id: string) => void;
}

const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkAsRead,
}: NotificationItemProps) {
  const { t, i18n } = useTranslation(["common", "pages"]);
  const localizedContent = useMemo(
    () => getLocalizedNotificationContent(notification, t),
    [notification, t]
  );
  const timeAgo = formatNotificationTimeAgo(
    notification.created_at,
    i18n.language
  );

  const isUnread = notification.status !== "read";
  const briefingPath = getAssistantBriefingPath(notification.data);

  return (
    <DropdownMenuItem
      key={notification.id}
      onClick={() => onMarkAsRead(notification.id)}
      className="cursor-pointer flex items-center gap-3 p-3 text-left focus:bg-muted/50 transition-colors group"
    >
      {getNotificationIcon(notification.notification_type, isUnread)}

      <div className="flex-1 min-w-0 pr-1 space-y-1 self-center">
        <div className="flex items-center gap-1.5">
          <p
            className={`text-xs sm:text-sm leading-tight ${isUnread ? "font-bold text-foreground" : "font-medium text-foreground/80"}`}
          >
            {truncate(localizedContent.title, MAX_TITLE_LENGTH)}
          </p>
          {isUnread && (
            <span className="size-1.5 rounded-full bg-primary shrink-0" />
          )}
        </div>

        <p className="text-xs text-muted-foreground line-clamp-2 leading-snug">
          {localizedContent.message}
        </p>

        <div className="flex items-center justify-between pt-1">
          <span className="text-[10px] text-muted-foreground/70 font-medium">
            {timeAgo}
          </span>
          <div className="flex gap-1.5 items-center">
            {typeof notification.data?.download_url === "string" ? (
              <Button
                variant="secondary"
                size="sm"
                className="h-6 text-[10px] px-2 shadow-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadNotificationFile(
                    notification.data?.download_url as string
                  );
                }}
              >
                <DownloadCloud className="size-3 mr-1" />
                {t("notifications_download", "Descargar")}
              </Button>
            ) : briefingPath ||
              typeof notification.data?.project_id === "string" ? (
              <Button
                variant="secondary"
                size="sm"
                className="h-6 text-[10px] px-2 shadow-sm"
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  to={
                    briefingPath ?? `/projects/${notification.data?.project_id}`
                  }
                >
                  <Eye className="size-3 mr-1" />
                  {t("notifications_view", "Ver")}
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </DropdownMenuItem>
  );
});
NotificationItem.displayName = "NotificationItem";

export const NotificationBell = () => {
  const { t } = useTranslation(["common", "pages"]);
  const realtimeNotificationToasts = useMemo(
    () =>
      createRealtimeNotificationToastBatcher({
        toast,
        t,
        localize: (notification) =>
          getLocalizedNotificationContent(notification, t),
      }),
    [t]
  );

  useEffect(
    () => () => realtimeNotificationToasts.dispose(),
    [realtimeNotificationToasts]
  );

  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAllRead,
    refresh,
    markAsRead,
  } = useNotifications({
    onRealtimeNotification: useCallback(
      (notification: NotificationSummary) => {
        realtimeNotificationToasts.push(notification);
      },
      [realtimeNotificationToasts]
    ),
    onProcessingUpdate: useCallback(
      (update: LayerProcessingUpdate) => {
        if (update.status === "complete") {
          toast.success(
            update.layer_name
              ? t("toast.tiles_ready_named", { name: update.layer_name })
              : t("toast.tiles_ready"),
            {
              description: update.tilejson_url,
              duration: 6000,
            }
          );
        } else if (update.status === "error") {
          const description =
            (update.processing?.error as string | undefined) ??
            t("toast.tile_generation_failed_description");
          toast.error(
            update.layer_name
              ? t("toast.tile_generation_failed_named", {
                  name: update.layer_name,
                })
              : t("toast.tile_generation_failed"),
            {
              description,
              duration: 8000,
            }
          );
        }
      },
      [t]
    ),
    onPostgisUpdate: useCallback(
      (update: LayerPostgisUpdate) => {
        if (update.status === "complete") {
          toast.success(
            update.layer_name
              ? t("toast.postgis_ready_named", { name: update.layer_name })
              : t("toast.postgis_ready"),
            {
              description: t("toast.postgis_import_finished"),
              duration: 6000,
            }
          );
        } else if (update.status === "error") {
          const description =
            (update.postgis_processing?.error as string | undefined) ??
            t("toast.postgis_import_failed_description");
          toast.error(
            update.layer_name
              ? t("toast.postgis_import_failed_named", {
                  name: update.layer_name,
                })
              : t("toast.postgis_import_failed"),
            {
              description,
              duration: 8000,
            }
          );
        }
      },
      [t]
    ),
  });

  const hasNotifications = notifications.length > 0;
  const unreadBadge = useMemo(() => {
    if (unreadCount <= 0) return null;
    const displayCount = unreadCount > 99 ? "99+" : unreadCount;
    return (
      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold leading-none text-destructive-foreground">
        {displayCount}
      </span>
    );
  }, [unreadCount]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-foreground"
          aria-label={t("notifications")}
        >
          <Bell className="size-5" />
          {unreadBadge}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[320px] sm:w-[360px] rounded-xl border border-border bg-white text-black shadow-xl dark:bg-zinc-950 dark:text-zinc-50"
      >
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="text-sm font-semibold">
            {t("notifications")}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-foreground"
              onClick={() => refresh()}
              disabled={loading}
              aria-label={t("notifications_refresh")}
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCcw className="size-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-foreground"
              onClick={() => markAllRead()}
              disabled={loading || unreadCount === 0}
              aria-label={t("notifications_mark_all_read")}
            >
              <CheckCheck className="size-4" />
            </Button>
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80">
          {error ? (
            <DropdownMenuItem className="h-24 cursor-default py-6 text-center text-sm text-destructive">
              {t("notifications_error")}
            </DropdownMenuItem>
          ) : loading && !hasNotifications ? (
            <div className="px-3 py-2 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
              ))}
            </div>
          ) : hasNotifications ? (
            <ScrollArea className="max-h-64">
              <div className="flex flex-col">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                  />
                ))}
              </div>
            </ScrollArea>
          ) : (
            <DropdownMenuItem className="h-24 cursor-default py-6 text-center text-sm text-muted-foreground">
              {t("notifications_empty")}
            </DropdownMenuItem>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem asChild className="p-0">
          <Link
            to="/notifications"
            className="w-full flex items-center justify-center py-3 px-4 cursor-pointer text-sm font-semibold text-primary hover:bg-primary/5 hover:text-primary transition-colors focus:bg-primary/5 rounded-b-xl"
          >
            {t("notifications_view_all")}
            <ArrowRight className="size-4 ml-2 opacity-70" />
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
